import { useMatomo } from '@datapunt/matomo-tracker-react';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'clsx';
import { setDefaultOptions } from 'date-fns';
import * as Locales from 'date-fns/locale';
import Cookies from 'js-cookie';
import React, { useEffect } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { Outlet, ScrollRestoration, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Flip, ToastContainer } from 'react-toastify';

import ChatWindow from '@components/common/ChatComponent/components/ChatWindow/ChatWindow';
import Loader from '@components/common/Loader/Loader';
import ModalProvider from '@components/modals/ModalProvider/ModalProvider';

import { dateFnsLocales } from '@constants/dateFnsLocales';
import { MODAL_NAMES } from '@constants/modalRenderers';

import useResetAllFilters from '@hooks/useResetAllFilters';

import { useAITranslateActions } from '@pages/ActionItem/store/useAITranslateStore';

import { isHostnameAws, isHostnameAzure } from '@services/apiClient';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerSetMutation } from '@services/useCustomerSetMutation';
import { useLanguageListQuery } from '@services/useLanguageListQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppError } from '@store/useAppStore';
import {
  selectAppPersistDarkMode,
  selectAppPersistOnBoardComplete,
  selectAppPersistOnBoardStart,
  selectAppPersistSidebarResponse,
  useAppPersistActions,
} from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import Error401 from './components/Error401/Error401';
import Error403 from './components/Error403/Error403';
import Sidebar from './components/Sidebar/Sidebar';
import useLanguage from './hooks/useLanguage';
import style from './style.module.scss';

const Root = () => {
  const { enableLinkTracking, trackPageView, pushInstruction } = useMatomo();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, redirectAiId } = useParams();

  const { setAppPersistOnBoardStart, setAppPersistOnBoardComplete } = useAppPersistActions();
  const { resetAllFilter } = useResetAllFilters();

  const onBoardComplete = selectAppPersistOnBoardComplete();
  const onBoardStart = selectAppPersistOnBoardStart();
  const sidebarResponse = selectAppPersistSidebarResponse();
  const error = selectAppError();

  const {
    data: profileData,
    isSuccess: profileIsSuccess,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
  } = useUserProfileQuery();

  useLanguage({ userLanguageIsCode: profileData?.user?.language_iso_code });

  const { data: assetTreeData, isLoading: assetTreeIsLoading } = useAssetTreeQuery();

  const { mutate: customerSetMutate, isLoading: customerSetIsLoading } = useCustomerSetMutation();

  const queryClient = useQueryClient();

  const isOneFacilityActive = !!assetTreeData?.facilities.length;

  const userPermissionsAccess = profileData?.userAccess?.appAccess;
  const darkMode = selectAppPersistDarkMode();

  const { setActiveModal } = useModalActions();
  const { setAITranslateTargetLanguage, setAITranslateProfileLanguageName } = useAITranslateActions();
  const { data: languageListData, isSuccess: isLanguageListSuccess } = useLanguageListQuery();

  enableLinkTracking();

  useEffect(() => {
    if (Cookies.get('autoTest')) {
      setAppPersistOnBoardStart(false);
      setAppPersistOnBoardComplete(true);
    }

    if (profileData) {
      const pathname = window.location.pathname;
      const storage = profileData?.customer?.storage;
      const profileIsoCode = profileData?.user?.language_iso_code;
      const lastSeen = profileData?.user?.last_seen;

      if (isHostnameAws && storage !== 'aws') {
        window.location.replace(APP_DOMAIN_AZURE + pathname);
      }

      if (isHostnameAzure && storage !== 'azure') {
        window.location.replace(APP_DOMAIN_AWS + pathname);
      }

      if (!onBoardComplete && !onBoardStart) {
        navigate(`/customer/${customerId}`);
        setAppPersistOnBoardStart(true);
      }

      if (onBoardStart) {
        setAppPersistOnBoardStart(false);
      }

      const newAiTypesModalVisited = Cookies.get('newAiTypesModalVisited', { domain: APP_COOKIE_DOMAIN });
      if (lastSeen && !newAiTypesModalVisited) {
        setActiveModal({ name: MODAL_NAMES.newActionItemTypesModal });
      }
      Cookies.set('newAiTypesModalVisited', '1', {
        expires: 365,
        domain: APP_COOKIE_DOMAIN,
      });

      setDefaultLocale(profileIsoCode);
      registerLocale(profileIsoCode, Locales[profileIsoCode]);
    }
  }, [profileData]);

  useEffect(() => {
    if (isLanguageListSuccess) {
      const profileIsoCode = profileData?.user?.language_iso_code;
      const profileLangObj = languageListData.find((lang) => lang.iso_code === profileIsoCode);
      const locale = dateFnsLocales[profileIsoCode];

      if (locale) {
        setDefaultOptions({ locale });
      }

      setAITranslateTargetLanguage(profileIsoCode);
      setAITranslateProfileLanguageName(profileLangObj?.original_name || profileLangObj?.name);
    }
  }, [profileData, languageListData, isLanguageListSuccess]);

  useEffect(() => {
    const handleWindowFocus = () => {
      const cookieCustomerId = Cookies.get(APP_COOKIE_CUSTOMER);
      if (
        !!cookieCustomerId &&
        !!profileData?.customer.customer_id &&
        +cookieCustomerId !== profileData?.customer.customer_id
      ) {
        queryClient.resetQueries();
        navigate(`/customer/${cookieCustomerId}`);
        resetAllFilter();
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [profileData]);

  useEffect(() => {
    if (location && !redirectAiId && (!customerId || customerId === 'undefined')) {
      navigate(`/customer/${profileData?.customer.customer_id || profileData?.user?.customer.customer_id}`);
    }
    if (
      customerId &&
      profileIsSuccess &&
      customerId !== 'undefined' &&
      +customerId !== profileData?.customer?.customer_id &&
      userPermissionsAccess &&
      location.state != 'SwitchCustomerModal'
    ) {
      customerSetMutate({ customer_id: customerId });
      resetAllFilter();
    }
  }, [customerId, profileIsSuccess, userPermissionsAccess]);

  useEffect(() => {
    if (!Cookies.get(APP_PRIVACY)) {
      setActiveModal({ name: MODAL_NAMES.privacyPolicyModal });
    }
  }, []);

  useEffect(() => {
    const contentBlock = document.getElementById('contentBlock');

    if (profileData?.user?.full_name) {
      pushInstruction('setUserId', profileData?.user?.full_name);
    }

    contentBlock && contentBlock.scrollTo(0, 0);

    trackPageView({
      documentTitle: 'Waites Dashboard',
      href: location.pathname,
    });
  }, [location, profileData?.user]);

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
  }, [darkMode]);

  if (assetTreeIsLoading || customerSetIsLoading || isProfileLoading) {
    return <Loader />;
  }

  if (error === '403') {
    return <Error403 />;
  }

  if ((!isProfileFetching && !userPermissionsAccess) || !isOneFacilityActive) {
    return (
      <>
        <Error401
          isStructureData={assetTreeData === null}
          permissions={userPermissionsAccess}
          locationsLength={assetTreeData?.locations.length}
          customerId={customerId}
          isOneFacilityActive={isOneFacilityActive}
        />
        <ModalProvider />
      </>
    );
  }

  return (
    <>
      <div className={cx(style.mainWrapper)}>
        <Sidebar />
        <ToastContainer
          hideProgressBar={true}
          autoClose={5000}
          transition={Flip}
          className={style.toastContainer}
        />

        <div
          className={cx(style.contentBlock, {
            [style.fullSidebar]: !sidebarResponse,
            [style.onBoardBlock]: onBoardStart,
          })}
          id="contentBlock"
        >
          <Outlet />
          <ScrollRestoration />
        </div>
        <ChatWindow />
      </div>
      <ModalProvider />
    </>
  );
};

export default Root;
